import { useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { get, post } from "common/helpers/HTTP";
import type {
  GenericSearchParam,
  LinkedInProfile,
  ProfileSearchParams,
  SearchParamWithImage,
} from "common/types";

async function getIndustries(
  workspaceId: string,
  accountId: string,
  keywords: string,
): Promise<GenericSearchParam[]> {
  return (
    await get(
      `workspaces/${workspaceId}/accounts/${accountId}/search/industries?keyword=${keywords}`,
    )
  ).industries;
}
async function getCompanies(
  workspaceId: string,
  accountId: string,
  keywords: string,
): Promise<SearchParamWithImage[]> {
  return (
    await get(
      `workspaces/${workspaceId}/accounts/${accountId}/search/companies?keyword=${keywords}`,
    )
  ).companies;
}
async function getLocations(
  workspaceId: string,
  accountId: string,
  keywords: string,
): Promise<GenericSearchParam[]> {
  return (
    await get(
      `workspaces/${workspaceId}/accounts/${accountId}/search/locations?keyword=${keywords}`,
    )
  ).locations;
}
async function getFollowers(
  workspaceId: string,
  accountId: string,
  keywords: string,
): Promise<LinkedInProfile[]> {
  return (
    await get(
      `workspaces/${workspaceId}/accounts/${accountId}/search/followers?keyword=${keywords}`,
    )
  ).profiles;
}
async function getSchools(
  workspaceId: string,
  accountId: string,
  keywords: string,
): Promise<SearchParamWithImage[]> {
  return (
    await get(
      `workspaces/${workspaceId}/accounts/${accountId}/search/schools?keyword=${keywords}`,
    )
  ).schools;
}
async function getSkills(
  workspaceId: string,
  accountId: string,
  keywords: string,
): Promise<GenericSearchParam[]> {
  return (
    await get(
      `workspaces/${workspaceId}/accounts/${accountId}/search/skills?keyword=${keywords}`,
    )
  ).skills;
}
async function getLanguages(
  workspaceId: string,
  accountId: string,
  keywords: string,
): Promise<GenericSearchParam[]> {
  return (
    await get(
      `workspaces/${workspaceId}/accounts/${accountId}/search/languages?keyword=${keywords}`,
    )
  ).languages;
}
async function getSuggestions(
  workspaceId: string,
  accountId: string,
  searchParams: Partial<ProfileSearchParams> = {},
) {
  return (
    await post(
      `workspaces/${workspaceId}/accounts/${accountId}/search/suggestions`,
      searchParams,
    )
  ).suggestions;
}

export default function useSearchSourceOptions({
  accountId,
  searchParams,
  companiesKeywords,
  locationsKeywords,
  followersKeywords,
  schoolsKeywords,
  industriesKeywords,
  skillsKeywords,
  languagesKeywords,
}: {
  accountId?: string;
  searchParams?: Partial<ProfileSearchParams>;
  companiesKeywords?: string;
  locationsKeywords?: string;
  followersKeywords?: string;
  schoolsKeywords?: string;
  industriesKeywords?: string;
  skillsKeywords?: string;
  languagesKeywords?: string;
} = {}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { leadListSourcesKeys } = getQueryKeys(workspaceId);

  const { data: industries, isLoading: isLoadingIndustries } = useQuery({
    queryKey: leadListSourcesKeys.searchOptions(
      accountId,
      "industries",
      industriesKeywords,
    ),
    queryFn: () => getIndustries(workspaceId, accountId, industriesKeywords),
    enabled: !!accountId && !!industriesKeywords,
  });

  const { data: companies, isLoading: isLoadingCompanies } = useQuery({
    queryKey: leadListSourcesKeys.searchOptions(
      accountId,
      "companies",
      companiesKeywords,
    ),
    queryFn: () => getCompanies(workspaceId, accountId, companiesKeywords),
    enabled: !!accountId && !!companiesKeywords,
  });

  const { data: locations, isLoading: isLoadingLocations } = useQuery({
    queryKey: leadListSourcesKeys.searchOptions(
      accountId,
      "locations",
      locationsKeywords,
    ),
    queryFn: () => getLocations(workspaceId, accountId, locationsKeywords),
    enabled: !!accountId && !!locationsKeywords,
  });

  const { data: schools, isLoading: isLoadingSchools } = useQuery({
    queryKey: leadListSourcesKeys.searchOptions(
      accountId,
      "schools",
      schoolsKeywords,
    ),
    queryFn: () => getSchools(workspaceId, accountId, schoolsKeywords),
    enabled: !!accountId && !!schoolsKeywords,
  });

  const { data: followers, isLoading: isLoadingFollowers } = useQuery({
    queryKey: leadListSourcesKeys.searchOptions(
      accountId,
      "followers",
      followersKeywords,
    ),
    queryFn: () => getFollowers(workspaceId, accountId, followersKeywords),
    enabled: !!accountId && !!followersKeywords,
  });

  const { data: skills, isLoading: isLoadingSkills } = useQuery({
    queryKey: leadListSourcesKeys.searchOptions(
      accountId,
      "skills",
      skillsKeywords,
    ),
    queryFn: () => getSkills(workspaceId, accountId, skillsKeywords),
    enabled: !!accountId && !!skillsKeywords,
  });

  const { data: languages, isLoading: isLoadingLanguages } = useQuery({
    queryKey: leadListSourcesKeys.searchOptions(
      accountId,
      "languages",
      languagesKeywords,
    ),
    queryFn: () => getLanguages(workspaceId, accountId, languagesKeywords),
    enabled: !!accountId && !!languagesKeywords,
  });

  const { data: suggestions, isLoading: isLoadingSuggestions } = useQuery({
    queryKey: leadListSourcesKeys.searchSuggestions(accountId),
    queryFn: () => getSuggestions(workspaceId, accountId, searchParams),
    enabled: !!accountId,
    staleTime: Infinity,
  });

  return {
    industries,
    isLoadingIndustries,
    companies,
    isLoadingCompanies,
    locations,
    isLoadingLocations,
    followers,
    isLoadingFollowers,
    schools,
    isLoadingSchools,
    skills,
    languages,
    isLoadingLanguages,
    suggestions,
    isLoadingSkills,
    isLoadingSuggestions,
    isLoading:
      isLoadingIndustries ||
      isLoadingCompanies ||
      isLoadingLocations ||
      isLoadingFollowers ||
      isLoadingSchools ||
      isLoadingSkills ||
      isLoadingLanguages ||
      isLoadingSuggestions,
  };
}
